var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Helpers from "./Helpers.js";
import DomHelper from "./DomHelper.js";
import ObjectHelper from "./ObjectHelper.js";
import FunctionHelper from "./FunctionHelper.js";
import BrowserHelper from "./BrowserHelper.js";
import Rectangle, { Point } from "./util/Rectangle.js";
import StringHelper from "./StringHelper.js";
import GlobalEvents from "../GlobalEvents.js";
const touchProperties = [
  "clientX",
  "clientY",
  "pageX",
  "pageY",
  "screenX",
  "screenY"
], isOption = {
  element: 1,
  thisObj: 1,
  once: 1,
  delegate: 1,
  delay: 1,
  capture: 1,
  passive: 1,
  throttled: 1,
  autoDetach: 1,
  expires: 1,
  block: 1,
  raw: 1,
  fromGlobal: 1
}, normalizedKeyNames = {
  Spacebar: "Space",
  Del: "Delete",
  Esc: "Escape",
  Left: "ArrowLeft",
  Up: "ArrowUp",
  Right: "ArrowRight",
  Down: "ArrowDown"
}, specialKeys = {
  Control: "ctrl",
  Alt: "alt",
  Shift: "shift",
  Meta: "meta"
}, specialKeyRe = /^(ctrl|shift|alt|meta)$/, eventProps = [
  "altKey",
  "bubbles",
  "button",
  "buttons",
  "cancelable",
  "clientX",
  "clientY",
  "ctrlKey",
  "layerX",
  "layerY",
  "metaKey",
  "pageX",
  "pageY",
  "returnValue",
  "screenX",
  "screenY",
  "shiftKey"
], powerSet = (...names) => names.reduce((a, v) => {
  for (let i = 0, n = a.length; i < n; ++i) {
    a.push(i ? `${a[i]}+${v}` : v);
  }
  return a;
}, [""]), letterKeyNames = Object.fromEntries("abcdefghijklmnopqrstuvwxyz".split("").map((c) => [c, c.toUpperCase()])), modifierKeys = powerSet("Ctrl", "Alt", "Meta", "Shift"), getModKeys = (ev) => modifierKeys[(ev.ctrlKey && 1) | (ev.altKey && 2) | (ev.metaKey && 4) | (ev.shiftKey && 8)], getKeyName = (event) => {
  const parts = [getModKeys(event)], { code, key } = event;
  parts[parts[0] ? 1 : 0] = (code == null ? void 0 : code.includes("Numpad")) ? code : letterKeyNames[key] || key;
  return parts.join("+");
}, {
  isSafari
} = BrowserHelper, wrapperMap = /* @__PURE__ */ new WeakMap(), fixTarget = (target) => (target == null ? void 0 : target.nodeType) === Element.TEXT_NODE ? target.parentElement : target;
const _EventHelper = class _EventHelper {
  static normalizeEvent(event) {
    return ObjectHelper.copyPropertiesIf(event, event.changedTouches[0] || event.touches[0], touchProperties);
  }
  /**
   * For use when synthesizing events from native DOM events. Copies valid properties from the passed
   * event into the destination object;
   * @param {Object} dest Destination object
   * @param {Event} event The event whose properties to copy
   * @returns {Object} An event construction object.
   * @internal
   */
  static copyEvent(dest, event) {
    return ObjectHelper.copyProperties(dest, event, eventProps);
  }
  /**
   * Returns the `[x, y]` coordinates of the event in the viewport coordinate system.
   * @param {Event} event The event
   * @returns {Number[]} The coordinate.
   */
  static getXY(event) {
    if (event.touches) {
      event = event.touches[0];
    }
    return [event.clientX, event.clientY];
  }
  /**
   * Returns the pixel distance between two mouse/touch/pointer events.
   * @param {Event} event1 The first event.
   * @param {Event} event2 The second event.
   * @returns {Number} The distance in pixels between the two events.
   */
  static getDistanceBetween(event1, event2, options) {
    const xy1 = EH.getXY(event1), xy2 = EH.getXY(event2), xDistance = xy1[0] - xy2[0], yDistance = xy1[1] - xy2[1];
    if (options == null ? void 0 : options.x) {
      return xDistance;
    }
    if (options == null ? void 0 : options.y) {
      return yDistance;
    }
    return Math.sqrt(Math.pow(xDistance, 2) + Math.pow(yDistance, 2));
  }
  /**
   * Returns a {@link Core.helper.util.Rectangle.Point} which encapsulates the `pageX/Y` position of the event.
   * May be used in {@link Core.helper.util.Rectangle} events.
   * @param {Event} event A browser mouse/touch/pointer event.
   * @returns {Core.helper.util.Rectangle.Point} The page point.
   */
  static getPagePoint(event) {
    return new Point(event.pageX, event.pageY);
  }
  /**
   * Returns a {@link Core.helper.util.Rectangle.Point} which encapsulates the `clientX/Y` position of the event.
   * May be used in {@link Core.helper.util.Rectangle} events.
   * @param {Event} event A browser mouse/touch/pointer event.
   * @returns {Core.helper.util.Rectangle.Point} The page point.
   */
  static getClientPoint(event) {
    return new Point(event.clientX, event.clientY);
  }
  /**
   * Add a listener or listeners to an element
   * The `options` parameter allows supplying options for the listener(s), for available options see {@link #typedef-ElementListenerConfig}.
   *
   * @param {EventTarget} element The event target to add a listener/listeners to.
   * @param {String|Object} eventName Either a string, being the name of the event to listen for,
   * or an options object containing event names and options as keys. See the options parameter
   * for details, or the {@link #function-on-static} method for details.
   * @param {Function} [handler] If the second parameter is a string event name, this is the handler function.
   * @param {ElementListenerConfig} [options] If the second parameter is a string event name, this is the options.
   * @returns {Function} A detacher function which removes all the listeners when called.
   */
  static addListener(element, eventName, handler, options) {
    if (element.nodeType) {
      if (typeof eventName === "string") {
        options = Object.assign({
          element,
          [eventName]: handler
        }, options);
      } else {
        options = Object.assign({
          element
        }, eventName);
      }
    } else {
      options = element;
    }
    return EH.on(options);
  }
  /**
   * Adds a listener or listeners to an element.
   * all property names other than the options listed below are taken to be event names,
   * and the values as handler specs.
   *
   * A handler spec is usually a function reference or the name of a function in the `thisObj`
   * option.
   *
   * But a handler spec may also be an options object containing a `handler` property which is
   * the function or function name, and local options, including `element` and `thisObj`
   * which override the top level options.
   *
   * The `options` parameter allows supplying options for the listener(s), for available options see {@link #typedef-ElementListenerConfig}.
   *
   *  Usage example
   *
   * ```javascript
   * construct(config) {
   *     super.construct(config);
   *
   *     // Add auto detaching event handlers to this Widget's reference elements
   *     EventHelper.on({
   *         element : this.iconElement,
   *         click   : '_handleIconClick',
   *         thisObj : this,
   *         contextmenu : {
   *             element : document,
   *             handler : '_handleDocumentContextMenu'
   *         }
   *     });
   * }
   *```
   *
   * The `click` handler on the `iconElement` calls `this._handleIconClick`.
   *
   * The `contextmenu` handler is added to the `document` element, but the `thisObj`
   * is defaulted in from the top `options` and calls `this._handleDocumentContextMenu`.
   *
   * Note that on touch devices, `dblclick` and `contextmenu` events are synthesized.
   * Synthesized events contain a `browserEvent` property containing the final triggering
   * event of the gesture. For example a synthesized `dblclick` event would contain a
   * `browserEvent` property which is the last `touchend` event. A synthetic `contextmenu`
   * event will contain a `browserEvent` property which the longstanding `touchstart` event.
   *
   * @param {ElementListenerConfig} options The full listener specification.
   * @returns {Function} A detacher function which removes all the listeners when called.
   */
  static on(options) {
    const element = options.element, thisObj = options.thisObj, handlerDetails = [];
    for (const eventName in options) {
      if (!isOption[eventName]) {
        let handlerSpec = options[eventName];
        if (typeof handlerSpec !== "object") {
          handlerSpec = {
            handler: handlerSpec
          };
        }
        const targetElement = handlerSpec.element || element;
        handlerDetails.push(EH.addElementListener(targetElement, eventName, handlerSpec, options));
      }
    }
    const detacher = () => {
      for (let handlerSpec, i = 0; i < handlerDetails.length; i++) {
        handlerSpec = handlerDetails[i];
        EH.removeEventListener(handlerSpec[0], handlerSpec[1], handlerSpec[2]);
      }
      handlerDetails.length = 0;
    };
    if (thisObj && options.autoDetach !== false) {
      thisObj.doDestroy = FunctionHelper.createInterceptor(thisObj.doDestroy, detacher, thisObj);
    }
    return detacher;
  }
  /**
   * Used internally to add a single event handler to an element.
   * @param {EventTarget} element The event target to add the handler to.
   * @param {String} eventName The name of the event to add a handler for.
   * @param {Function|String|Object} handlerSpec Either a function to call, or
   * the name of a function to call in the `thisObj`, or an object containing
   * the handler local options.
   * @param {Function|String} [handlerSpec.handler] Either a function to call, or
   * the name of a function to call in the `thisObj`.
   * @param {EventTarget} [handlerSpec.element] Optionally a local event target for the listener.
   * @param {Object} [handlerSpec.thisObj] A local `this` specification for the handler.
   * @param {Object} defaults The `options` parameter from the {@link #function-addListener-static} call.
   * @private
   */
  static addElementListener(element, eventName, handlerSpec, defaults) {
    const handler = EH.createHandler(element, eventName, handlerSpec, defaults), { spec } = handler, expires = handlerSpec.expires || defaults.expires, options = spec.capture != null || spec.passive != null ? {
      capture: spec.capture,
      passive: spec.passive
    } : void 0;
    element.addEventListener(eventName, handler, options);
    if (expires) {
      const thisObj = handlerSpec.thisObj || defaults.thisObj, delayable = (thisObj == null ? void 0 : thisObj.isDelayable) ? thisObj : globalThis, { alt } = expires, delay = alt ? expires.delay : expires, { spec: spec2 } = handler;
      spec2.expires = expires;
      spec2.timerId = delayable[typeof delay === "number" ? "setTimeout" : "requestAnimationFrame"](() => {
        spec2.timerId = null;
        EH.removeEventListener(element, eventName, handler);
        if (alt && !handler.called) {
          (typeof alt === "string" ? thisObj[alt] : alt).call(thisObj);
        }
      }, delay, `listener-timer-${performance.now()}`);
    }
    return [element, eventName, handler, options];
  }
  // composedPath throws in salesforce
  // https://github.com/bryntum/support/issues/4432
  static getComposedPathTarget(event) {
    var _a;
    return event.composedPath()[0] || ((_a = event.path) == null ? void 0 : _a[0]);
  }
  static fixEvent(event, fromGlobal) {
    var _a, _b;
    if (event.isBryntumEvent) {
      return event;
    }
    if (wrapperMap.has(event)) {
      return wrapperMap.get(event);
    }
    const { ctrlKey, metaKey, type, target } = event, deepTarget = this.getComposedPathTarget(event), isOpenWC = ((_b = (_a = deepTarget == null ? void 0 : deepTarget.getRootNode) == null ? void 0 : _a.call(deepTarget)) == null ? void 0 : _b.mode) === "open", eventWrapper = {
      set(event2, prop, value) {
        var _a2;
        if (prop in event2 && ((_a2 = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(event2), "type")) == null ? void 0 : _a2.set)) {
          return Reflect.set(event2, prop, value);
        } else {
          eventWrapper[prop] = value;
          return true;
        }
      },
      get(event2, prop) {
        switch (prop) {
          case "fixed":
          case "isBryntumEvent":
            return true;
          case "key":
            return normalizedKeyNames[event2.key] || event2.key;
          case "code":
            return event2.key === " " && !event2.code ? "Space" : event2.code;
          case "ctrlKey":
            return BrowserHelper.isMac && metaKey !== ctrlKey ? metaKey : ctrlKey;
          case "metaKey":
            return BrowserHelper.isMac && metaKey !== ctrlKey ? ctrlKey : metaKey;
          case "virtualKey":
            return getKeyName(result);
          case "virtualModKeys":
            return getModKeys(result);
          case "borderOffsetX":
            return !isNaN(event2.offsetX) ? event2.offsetX + (isSafari ? 0 : parseInt(target.ownerDocument.defaultView.getComputedStyle(fixTarget(target)).getPropertyValue("border-left-width"))) : void 0;
          case "borderOffsetY":
            return !isNaN(event2.offsetY) ? event2.offsetY + (isSafari ? 0 : parseInt(target.ownerDocument.defaultView.getComputedStyle(fixTarget(target)).getPropertyValue("border-top-width"))) : void 0;
          case "target":
            return isOpenWC ? deepTarget : fixTarget(event2.target);
          case "relatedTarget":
            return fixTarget(event2.relatedTarget);
          case "originalTarget":
            return target;
          case "deepTarget":
            return deepTarget;
          case "currentTarget":
            return eventWrapper.delegatedTarget || event2.currentTarget;
          case "browserEvent":
            return event2;
          default:
            if (!(prop in eventWrapper)) {
              const result2 = Reflect.get(event2, prop);
              return typeof result2 === "function" ? result2.bind(event2) : result2;
            } else {
              return eventWrapper[prop];
            }
        }
      },
      has(event2, key) {
        return key in event2 || key in eventWrapper;
      }
    };
    const result = new Proxy(event, eventWrapper);
    wrapperMap.set(event, result);
    if (type.startsWith("touch")) {
      this.normalizeEvent(result);
    }
    if (!fromGlobal) {
      switch (type) {
        case "keydown":
          GlobalEvents.currentKeyDown = result;
          break;
        case "mousedown":
          GlobalEvents.currentMouseDown = result;
          break;
        case "pointerdown":
          GlobalEvents.currentPointerDown = result;
          break;
        case "touchstart":
          GlobalEvents.currentTouch = result;
      }
    }
    return result;
  }
  static createHandler(element, eventName, handlerSpec, defaults) {
    const delay = handlerSpec.delay || defaults.delay, throttled = handlerSpec.throttled || defaults.throttled, block = handlerSpec.block || defaults.block, once = "once" in handlerSpec ? handlerSpec.once : defaults.once, capture = "capture" in handlerSpec ? handlerSpec.capture : defaults.capture, passive = "passive" in handlerSpec ? handlerSpec.passive : defaults.passive, delegate = "delegate" in handlerSpec ? handlerSpec.delegate : defaults.delegate, fromGlobal = "fromGlobal" in handlerSpec ? handlerSpec.fromGlobal : defaults.fromGlobal, wrappedFn = handlerSpec.handler, expires = handlerSpec.expires, thisObj = handlerSpec.thisObj || defaults.thisObj;
    let handler = (event, ...args) => {
      if (EH.playingDemo && event.isTrusted) {
        return;
      }
      if (thisObj == null ? void 0 : thisObj.isDestroyed) {
        return;
      }
      handler.called = true;
      (typeof wrappedFn === "string" ? thisObj[wrappedFn] : wrappedFn).call(thisObj, EH.fixEvent(event, fromGlobal), ...args);
    };
    if (block) {
      const wrappedFn2 = handler;
      let lastCallTime, lastTarget;
      handler = (e, ...args) => {
        const now = performance.now();
        if (!lastCallTime || e.target !== lastTarget || now - lastCallTime > block) {
          lastTarget = e.target;
          lastCallTime = now;
          wrappedFn2(e, ...args);
        }
      };
    }
    if (delay != null) {
      const wrappedFn2 = handler, delayable = (thisObj == null ? void 0 : thisObj.setTimeout) ? thisObj : globalThis;
      handler = (...args) => {
        delayable.setTimeout(() => {
          wrappedFn2(...args);
        }, delay);
      };
    }
    if (throttled != null) {
      let alt, buffer = throttled;
      if (throttled.buffer) {
        alt = (e) => {
          return throttled.alt.call(EH, EH.fixEvent(e, fromGlobal));
        };
        buffer = throttled.buffer;
      }
      if (thisObj == null ? void 0 : thisObj.isDelayable) {
        handler = thisObj.throttle(handler, {
          delay: buffer,
          throttled: alt
        });
      } else {
        handler = FunctionHelper.createThrottled(handler, buffer, thisObj, null, alt);
      }
    }
    if (once) {
      const wrappedFn2 = handler;
      handler = (...args) => {
        EH.removeEventListener(element, eventName, handler);
        wrappedFn2(...args);
      };
    }
    if (delegate) {
      const wrappedFn2 = handler;
      handler = (event, ...args) => {
        var _a, _b;
        event = EH.fixEvent(event, fromGlobal);
        const delegatedTarget = (_b = (_a = event.target).closest) == null ? void 0 : _b.call(_a, delegate);
        if (!delegatedTarget) {
          return;
        }
        event.delegatedTarget = delegatedTarget;
        wrappedFn2(event, ...args);
      };
    }
    if (handlerSpec.thisObj && handlerSpec.autoDetach !== false) {
      thisObj.doDestroy = FunctionHelper.createInterceptor(thisObj.doDestroy, () => EH.removeEventListener(element, eventName, handler), thisObj);
    }
    handler.spec = {
      delay,
      throttled,
      block,
      once,
      thisObj,
      capture,
      expires,
      passive,
      delegate
    };
    return handler;
  }
  static removeEventListener(element, eventName, handler) {
    const { expires, timerId, thisObj, capture } = handler.spec;
    if ((expires == null ? void 0 : expires.alt) && timerId) {
      const delayable = (thisObj == null ? void 0 : thisObj.isDelayable) ? thisObj : globalThis;
      delayable[typeof expires.delay === "number" ? "clearTimeout" : "cancelAnimationFrame"](timerId);
    }
    element.removeEventListener(eventName, handler, capture);
  }
  /**
   * Calls a callback when the described animation completes.
   *
   * @param {Object} detail
   * @param {HTMLElement} detail.element The element which is being animated.
   * @param {String|RegExp} [detail.animationName] The name of the animation to wait for.
   * @param {String} [detail.property] If no `animationName` specified, the CSS property
   * which is being animated.
   * @param {Function} detail.handler The function to call on animation end.
   * @param {Number} [detail.duration] Optional fallback time to wait until calling the callback.
   * @param {Object} [detail.thisObj] The `this` reference to call the callback with.
   * @param {Array} [detail.args] Optional arguments to call the callback with.
   * @param {Core.mixin.Delayable} [detail.timerSource] A Delayable to provide the fallback timeout.
   * @param {Boolean} [detail.runOnDestroy] If `timerSource` is a {@link Core.mixin.Delayable},
   * `true` to invoke the callback if it is destroyed during the animation.
   * @returns {Function} a function which detaches the animation end listener.
   */
  static onTransitionEnd({
    element,
    animationName,
    property,
    handler,
    mode = animationName ? "animation" : "transition",
    duration = DomHelper[`get${mode === "transition" ? "Property" : ""}${StringHelper.capitalize(mode)}Duration`](element, property),
    timeout,
    thisObj = globalThis,
    args = [],
    timerSource,
    runOnDestroy
  }) {
    let timerId, maxDurationTimerId;
    timerSource = timerSource || (thisObj.isDelayable ? thisObj : globalThis);
    const { isDelayable } = timerSource, now = performance.now(), transitionTimerId = `transitionTimer${now}`, timeoutTimerId = `timeoutTimer${now}`, callbackArgs = [element, property, ...args], doCallback = () => {
      var _a;
      detacher();
      if (!thisObj.isDestroyed) {
        if ((_a = thisObj.$meta) == null ? void 0 : _a.class.isBase) {
          thisObj.callback(handler, thisObj, callbackArgs);
        } else {
          handler.apply(thisObj, callbackArgs);
        }
      }
    }, detacher = EH.on({
      element,
      [`${mode}end`]({ animationName: endedAnimation, propertyName, target }) {
        var _a, _b;
        if (target === element) {
          if (propertyName === property || (endedAnimation == null ? void 0 : endedAnimation.match(animationName))) {
            if (timerId) {
              if (maxDurationTimerId) {
                (_a = timerSource.clearTimeout) == null ? void 0 : _a.call(timerSource, isDelayable ? timeoutTimerId : maxDurationTimerId);
              }
              (_b = timerSource.clearTimeout) == null ? void 0 : _b.call(timerSource, isDelayable ? transitionTimerId : timerId);
              timerId = null;
            }
            doCallback();
          }
        }
      }
    });
    if (timeout) {
      maxDurationTimerId = timerSource.setTimeout(doCallback, Math.max(timeout, (duration || 0) + 100), timeoutTimerId);
    }
    if (duration != null) {
      timerId = timerSource.setTimeout(doCallback, duration + 50, transitionTimerId, runOnDestroy);
    }
    return detacher;
  }
  /**
   * Waits for the described animation completes.
   *
   * @param {Object} config
   * @param {HTMLElement} config.element The element which is being animated.
   * @param {String|RegExp} [config.animationName] The name of the animation to wait for.
   * @param {String} [config.property] If no `animationName` specified, the CSS property
   * which is being animated.
   * @param {Number} [config.duration] Optional fallback time to wait until calling the callback.
   * @param {Core.mixin.Delayable} [config.timerSource] A Delayable to provide the fallback timeout.
   * @param {Boolean} [config.runOnDestroy] If `timerSource` is a {@link Core.mixin.Delayable},
   * `true` to invoke the callback if it is destroyed during the animation.
   * @async
   */
  static async waitForTransitionEnd(config) {
    return new Promise((resolve) => {
      config.handler = resolve;
      _EventHelper.onTransitionEnd(config);
    });
  }
  /**
   * Private function to wrap the passed function. The returned wrapper function to be used as
   * a `touchend` handler which will call the passed function passing a fabricated `dblclick`
   * event if there is a `click` within 300ms.
   * @param {Element} element element
   * @param {String|Function} handler The handler to call.
   * @param {Object} thisObj The owner of the function.
   * @private
   */
  static createDblClickWrapper(element, handler, thisObj) {
    let startId, secondListenerDetacher, tapholdTimer;
    return () => {
      if (!secondListenerDetacher) {
        secondListenerDetacher = EH.on({
          element,
          // We only get here if a touchstart arrives within 300ms of a click
          touchstart: (secondStart) => {
            startId = secondStart.changedTouches[0].identifier;
            secondStart.preventDefault();
          },
          touchend: (secondClick) => {
            if (secondClick.changedTouches[0].identifier === startId) {
              secondClick.preventDefault();
              clearTimeout(tapholdTimer);
              startId = secondListenerDetacher = null;
              const targetRect = Rectangle.from(secondClick.changedTouches[0].target, null, true), offsetX = secondClick.changedTouches[0].pageX - targetRect.x, offsetY = secondClick.changedTouches[0].pageY - targetRect.y, dblclickEventConfig = Object.assign({
                browserEvent: secondClick
              }, secondClick), dblclickEvent = new MouseEvent("dblclick", dblclickEventConfig);
              Object.defineProperties(dblclickEvent, {
                target: { value: secondClick.target },
                offsetX: { value: offsetX },
                offsetY: { value: offsetY }
              });
              if (typeof handler === "string") {
                handler = thisObj[handler];
              }
              handler.call(thisObj, dblclickEvent);
            }
          },
          once: true
        });
        tapholdTimer = setTimeout(() => {
          secondListenerDetacher();
          startId = secondListenerDetacher = null;
        }, EH.dblClickTime);
      }
    };
  }
  /**
   * Handles various inputs to figure out the name of the special key of the event.
   *
   * ```javascript
   * EventHelper.toSpecialKey('ctrl') // 'ctrlKey'
   * EventHelper.toSpecialKey(true)   // 'ctrlKey'
   * EventHelper.toSpecialKey(false)  // false
   * EventHelper.toSpecialKey('foo')  // false
   * ```
   *
   * @param {*} value User input value to process.
   * @param {String} defaultValue Default value to fall back to if `true` value is passed.
   * @returns {Boolean|String} Returns `false` if provided value cannot be converted to special key and special key
   * name otherwise.
   * @internal
   */
  static toSpecialKey(value, defaultValue = "ctrlKey") {
    let result = false;
    if (value === true) {
      result = defaultValue;
    } else if (typeof value === "string") {
      value = value.toLowerCase();
      if (value.match(specialKeyRe)) {
        result = `${value}Key`;
      }
    }
    return result;
  }
  /**
   * If keyup event is triggered when special key is pressed, we don't get special key value from properties like
   * `ctrlKey`. Instead we need to read `event.key`. That property uses full name and we use abbreviations, so we
   * need to convert the key.
   * @param {String} code
   * @returns {String}
   * @internal
   */
  static specialKeyFromEventKey(code) {
    return specialKeys[code] || "no-special-key";
  }
};
__publicField(_EventHelper, "$name", "EventHelper");
/**
 * DOM event to trigger name mapping.
 * @internal
 */
__publicField(_EventHelper, "eventNameMap", {
  mousedown: "MouseDown",
  mouseup: "MouseUp",
  click: "Click",
  dblclick: "DblClick",
  contextmenu: "ContextMenu",
  mouseenter: "MouseEnter",
  mouseleave: "MouseLeave",
  mousemove: "MouseMove",
  mouseover: "MouseOver",
  mouseout: "MouseOut",
  keyup: "KeyUp",
  keydown: "KeyDown",
  keypress: "KeyPress"
});
let EventHelper = _EventHelper;
Helpers.register(EventHelper);
const EH = EventHelper;
EH.longPressTime = 700;
EH.dblClickTime = 300;
if (BrowserHelper.isTouchDevice) {
  EH.on({
    element: document,
    touchmove: (event) => {
      if (event.target.closest(".b-dragging")) {
        event.preventDefault();
      }
    },
    passive: false,
    capture: true
  });
}
EventHelper._$name = "EventHelper";
export {
  EventHelper as default
};
